import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, useParams } from 'react-router'

import { baseApi } from '~/shell/api/rtk/baseApi'
import { customerHolder } from '~/shell/api/rtk/customer'
import { getEnabledProductApps } from '~/shell/auth/useEnabledProducts'
import { AppStateScope } from '~/shell/redux/AppStateScope'
import { forbiddenCleared } from '~/shell/redux/forbiddenSlice'
import { type RootState } from '~/shell/redux/store'
import { setLastApp } from '~/shell/storage/lastApp'
import { setLastCustomer } from '~/shell/storage/lastCustomer'

import { AppResolutionRedirect } from './AppResolutionRedirect'
import { AppHeader } from './header/AppHeader'
import { useActiveApp } from './useActiveApp'

/**
 * Everything scoped to one customer, mounted fresh for each.
 *
 * Its only job is the teardown: when AppStateScope replaces this instance, the cleanup
 * discards the cached API responses and any recorded 403, so neither crosses a customer boundary.
 * Doing it in cleanup rather than in the parent's effect is what makes the ordering work — react runs
 * an outgoing subtree's cleanups before the incoming subtree's effects, and it is those effects that
 * subscribe the queries. Resetting afterwards would wipe the new customer's in-flight requests
 * instead of the old customer's stale ones.
 *
 * Nothing happens on the first mount: there is no previous customer, so there is nothing stale to
 * clear.
 */
const CustomerScope = () => {
  const dispatch = useDispatch()

  useEffect(
    () => () => {
      dispatch(baseApi.util.resetApiState())
      // A new customer selection starts authorized; a 403 recorded against the previous one says
      // nothing about this one.
      dispatch(forbiddenCleared())
    },
    [dispatch],
  )

  return (
    <>
      <AppHeader />
      <Outlet />
    </>
  )
}

/**
 * Layout for every customer-scoped route (`/c/:customer/`): the app header above the routed page,
 * plus the customer plumbing the queries beneath it depend on.
 *
 * Responsibilities:
 *   - Publish the route's customer to the holder bridge, so the out-of-React RTK Query base query
 *     can scope requests it is not passed a customer for. Done during render rather than in an
 *     effect because the queries beneath fire from their own effects, and react runs a child's
 *     effects before its parent's: an effect here would set the holder *after* the first requests
 *     of a newly mounted subtree had already read it.
 *   - Remount the whole subtree on a customer switch through AppStateScope, so nothing
 *     belonging to the previous one survives: the queries resubscribe and refetch, and per-customer
 *     component state — scroll position, filters, an open flyout — is discarded rather than
 *     reinterpreted against different data. Clearing the cache alone would not do this;
 *     `resetApiState` empties the store but leaves already-mounted hooks subscribed, so the previous
 *     customer's data would stay on screen until something else forced a refetch.
 *   - Persist the active customer as the last-viewed default, which is what `/` and the app-entry
 *     URLs (`/arc`, `/scout`) resolve against. Written here rather than at the picker, because a
 *     customer is equally chosen by typing a URL.
 *   - Persist the active app the same way, and for the same reason: an app is equally chosen by
 *     typing a URL as by the switcher. Written only once the app is confirmed enabled, so the
 *     preference never records an app the next visit would have to reject.
 *   - Redirect an app the session may not reach through the shared landing chain, so a URL naming a
 *     `disabled` or unpermitted app lands in an app the session can open rather than on the header
 *     over an empty body. This is the recognized-but-unavailable half of Atlas ADR-0015; `AppFallback`
 *     answers the bare-customer and unknown-segment halves through the same resolver, so all three
 *     share one precedence order. Gating here rather than at the mount route means the unavailable
 *     app's chunk has already been fetched by the time this redirects, which Atlas ADR-0015 accepts: the
 *     app never mounts, so it costs bandwidth rather than data.
 *   - Redirect to the unauthorized page when a customer-scoped request has been rejected with a
 *     server-enforced 403. The `:customer` segment is only a selector; the atlas service's
 *     `authorize_customer_access` is the real boundary, so a customer this session may not reach is
 *     caught here from the 403 rather than blocked client-side. Dormant until real customer-scoped
 *     queries exist.
 */
export const CustomerLayout = () => {
  const { customer } = useParams<{ customer: string }>()
  const activeApp = useActiveApp()
  const isActiveAppEnabled = getEnabledProductApps().some(
    (app) => app.id === activeApp,
  )
  const isForbidden = useSelector(
    (state: RootState) => state.forbidden.isForbidden,
  )

  if (customer !== undefined) {
    customerHolder.set(customer)
  }

  useEffect(() => {
    if (customer !== undefined) {
      setLastCustomer(customer)
    }
  }, [customer])

  useEffect(() => {
    if (activeApp !== undefined && isActiveAppEnabled) {
      setLastApp(activeApp)
    }
  }, [activeApp, isActiveAppEnabled])

  if (isForbidden) {
    return <Navigate replace to="/unauthorized" />
  }

  if (
    customer !== undefined &&
    activeApp !== undefined &&
    !isActiveAppEnabled
  ) {
    return (
      <AppResolutionRedirect customer={customer} requestedAppId={activeApp} />
    )
  }

  return (
    <AppStateScope customer={customer ?? ''}>
      <CustomerScope />
    </AppStateScope>
  )
}
